import { useEffect } from 'react';
import { ArrowLeftIcon, ArrowsRightLeftIcon, SparklesIcon } from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import {
  DetailGroup,
  getBottomDetailHeader,
  getRowDetailContextLabel,
  isSentenceHtmlDetailKey,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
  SHOW_HISTORIAL_SCORE,
  type BottomDetailTab,
} from '../utils/searchResultsHelpers';
import TabSelector from '@/global/TabSelector/TabSelector';
import { HistoricalScoreTabContent } from './HistoricalScoreTabContent';
import { getDetailValueLink, openEntityInfoPanel } from '../utils/detailValueLinks';
import { BottomDetailValueWithTooltip } from './BottomDetailValueWithTooltip';
import { GtpTabContent } from './GtpTabContent';
import { CanopyTabContent } from './CanopyTabContent';
import { getGtpItemsFromRow } from '@/utils/formatGtpList';
import { isCanopyEnabled } from '@/config/canopy';
import { isEvidenceProfileEnabled } from '@/config/evidenceProfile';
import { EvidenceProfileTabContent } from './EvidenceProfileTabContent';
import AcademicPlanFeatureGate from '@/components/Modal/AcademicPlanFeatureGate';
import { isAcademicPlanFeatureRestricted } from '@/utils/premiumPlanFeatures';
import { trackRowDetailTabClick } from '@/utils/analytics';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface FullRecordDetailViewProps {
  bottomDetailRow: any;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  hasLeftPanel?: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  handleDownloadRowDetail: () => void;
  bottomDetailTab: BottomDetailTab;
  setBottomDetailTab: (tab: BottomDetailTab) => void;
  ensureTablesViewForDrilldown?: () => void;
  onRevealEvidenceTable?: () => void;
  isAcademic?: boolean;
  isAuthenticated?: boolean;
  gtpOnly?: boolean;
  bottomDetailCellValues?: Record<string, unknown> | null;
  onClose?: () => void;
}

export function FullRecordDetailView({
  bottomDetailRow,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  hasLeftPanel = false,
  searchParams,
  setSearchParams,
  handleDownloadRowDetail,
  bottomDetailTab,
  setBottomDetailTab,
  ensureTablesViewForDrilldown,
  onRevealEvidenceTable,
  isAcademic = false,
  isAuthenticated = false,
  gtpOnly = false,
  bottomDetailCellValues = null,
  onClose,
}: FullRecordDetailViewProps) {
  useEffect(() => {
    if (!bottomDetailRow) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [bottomDetailRow]);

  if (!bottomDetailRow) return null;
  const renderEntryValue = (entry: { key: string; value: unknown }) => {
    const linkedValue = getDetailValueLink(entry.key, entry.value);
    if (linkedValue?.type === 'external') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <a
          className="min-w-0 break-words font-semibold text-pink underline underline-offset-2 sm:overflow-hidden sm:text-ellipsis sm:whitespace-nowrap"
          href={linkedValue.href}
          target="_blank"
          rel="noopener noreferrer"
          title={textValue}
        >
          {textValue}
        </a>
      );
    }
    if (linkedValue?.type === 'entity') {
      const textValue = normalizeDetailValue(entry.value, entry.key);
      return (
        <button
          type="button"
          className="min-w-0 break-words bg-transparent p-0 text-left font-semibold text-pink underline underline-offset-2 sm:overflow-hidden sm:text-ellipsis sm:whitespace-nowrap"
          title={textValue}
          onClick={() => openEntityInfoPanel(linkedValue.entityView, linkedValue.item)}
        >
          {textValue}
        </button>
      );
    }
    const isSentenceHtml = isSentenceHtmlDetailKey(entry.key);
    const sentenceHtml = isSentenceHtml && typeof entry.value === 'string' ? entry.value.trim() : '';
    return sentenceHtml ? (
      <BottomDetailValueWithTooltip html={sentenceHtml} allowCopy />
    ) : (
      <span
        className="min-w-0 break-words font-semibold text-slate-800 sm:overflow-hidden sm:text-ellipsis sm:whitespace-nowrap"
        title={normalizeDetailValue(entry.value, entry.key)}
      >
        {normalizeDetailValue(entry.value, entry.key)}
      </span>
    );
  };

  const marginRight = isDesktop
    ? isSidebarOpen
      ? typeof sidebarWidth === 'number'
        ? sidebarWidth + 20
        : `calc(${sidebarWidth} + 20px)`
      : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX + 20
    : undefined;
  const marginLeft = isDesktop ? (hasLeftPanel ? 356 : 16) : undefined;

  const normalizedTab = String(tab ?? '').toUpperCase();
  const gtpFromRow = getGtpItemsFromRow(bottomDetailRow);
  const gtpFromCells = gtpFromRow.length === 0 ? getGtpItemsFromRow(bottomDetailCellValues) : [];
  const gtpItems = gtpFromRow.length > 0 ? gtpFromRow : gtpFromCells;
  const hasGtpData = gtpItems.length > 0;
  const showGtpTab = hasGtpData || normalizedTab === 'GDA' || normalizedTab === 'GENES';
  const showHistoricalScoreTab =
    !gtpOnly && ((SHOW_HISTORIAL_SCORE && normalizedTab === 'GDA') || normalizedTab === 'VDA');
  const showEvidenceProfileTab =
    isEvidenceProfileEnabled && !gtpOnly && (normalizedTab === 'GDA' || normalizedTab === 'VDA');
  const showGtpTabOption = !gtpOnly && showGtpTab;
  const showCanopyTab =
    isCanopyEnabled &&
    !gtpOnly &&
    (normalizedTab === 'GDA' ||
      normalizedTab === 'VDA' ||
      normalizedTab === 'DDA' ||
      normalizedTab === 'DISEASES');
  const showDetailTabs =
    showHistoricalScoreTab || showEvidenceProfileTab || showGtpTabOption || showCanopyTab;
  const isPremiumDetailFeatureRestricted = isAcademicPlanFeatureRestricted(isAcademic, isAuthenticated);
  let activeBottomDetailTab: BottomDetailTab = bottomDetailTab;
  if (gtpOnly) {
    activeBottomDetailTab = 'gtp';
  } else if (bottomDetailTab === 'evidence' && !showEvidenceProfileTab) {
    activeBottomDetailTab = 'summary';
  } else if (bottomDetailTab === 'gtp' && !showGtpTab) {
    activeBottomDetailTab = 'summary';
  } else if (bottomDetailTab === 'canopy' && !showCanopyTab) {
    activeBottomDetailTab = 'summary';
  }

  const tabOptions = [
    { label: 'Summary', value: 'summary' },
    ...(showHistoricalScoreTab ? [{ label: 'Historical score', value: 'historical' }] : []),
    ...(showEvidenceProfileTab ? [{ label: 'Evidence profile', value: 'evidence' }] : []),
    ...(showGtpTabOption ? [{ label: 'GTAP', value: 'gtp' }] : []),
    ...(showCanopyTab ? [{ label: 'Canopy', value: 'canopy' }] : []),
  ];

  const contextLabel = getRowDetailContextLabel(tab as string);
  const detailHeader = getBottomDetailHeader(activeBottomDetailTab, relationSummary, contextLabel, {
    ...((bottomDetailRow ?? {}) as Record<string, unknown>),
    ...((bottomDetailCellValues ?? {}) as Record<string, unknown>),
  });

  return (
    <div
      className="mt-2 mx-2 mb-4 min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm sm:mt-4 sm:mx-4"
      style={{
        ...(marginRight != null ? { marginRight } : {}),
        ...(marginLeft != null ? { marginLeft } : {}),
        transition: 'margin-right 0.3s ease-out, margin-left 0.3s ease-out',
      }}
    >
      <button
        type="button"
        className="mb-3 flex w-full items-center p-3 text-start text-sm font-medium text-slate-600 shadow-sm sm:p-4"
        onClick={() => {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete('fullRecord');
          setSearchParams(nextParams);
        }}
      >
        <ArrowLeftIcon className="w-4 h-4 mr-2" strokeWidth={2} /> Back to results
      </button>
      <div className="mb-3 mx-2 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-gradient-to-r from-pink-50 to-white p-3 sm:m-4 sm:flex-row sm:items-center sm:justify-between sm:p-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5 shrink-0 text-pink" />
            <span className="text-xs font-normal text-gray-400 tracking-widest text-uppercase">
              {detailHeader.contextLabel}
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span
                className={`break-words text-xl font-semibold sm:text-3xl ${
                  activeBottomDetailTab === 'canopy' ? '' : 'text-uppercase'
                }`}
              >
                {detailHeader.primaryEntity}
              </span>
              {detailHeader.showRelationArrow && (
                <>
                  <ArrowsRightLeftIcon className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="break-words text-lg font-normal sm:text-3xl">
                    {detailHeader.secondaryEntity}
                  </span>
                </>
              )}
            </div>
          </div>
          {activeBottomDetailTab !== 'gtp' && activeBottomDetailTab !== 'canopy' && (
            <div>
              <span className="text-xs font-normal text-gray-400">{relationSummary.diseaseType}</span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 mr-3">
          <SharedBadge
            text={`Source: ${source}`}
            icon={<SparklesIcon className="w-4 h-4 mr-1" />}
            variant="primaryLight"
            className="mb-0"
          />
          <SharedButton type="button" variant="primaryOutline" size="xs" onClick={handleDownloadRowDetail}>
            Download detail
          </SharedButton>
        </div>
      </div>
      {showDetailTabs && (
        <div
          className="mx-2 border-b border-slate-200 px-2 py-2 sm:mx-4 sm:px-4"
          role="tablist"
          aria-label="Record detail sections"
        >
          <TabSelector
            options={tabOptions}
            variant="primary"
            size="2xs"
            value={activeBottomDetailTab}
            onChange={v => {
              const nextTab = v as BottomDetailTab;
              trackRowDetailTabClick(nextTab);
              setBottomDetailTab(nextTab);
            }}
          />
        </div>
      )}
      {activeBottomDetailTab === 'gtp' ? (
        <div className="ml-2 mr-5 mb-4 mt-2 p-3 sm:mx-4 sm:p-4">
          <AcademicPlanFeatureGate featureName="GTAP" isRestricted={isPremiumDetailFeatureRestricted}>
            <GtpTabContent
              row={bottomDetailRow}
              cellValues={bottomDetailCellValues}
              title="Gene Therapeutic Area Profile"
            />
          </AcademicPlanFeatureGate>
        </div>
      ) : activeBottomDetailTab === 'canopy' && showCanopyTab ? (
        <div className="ml-2 mr-5 mb-4 mt-2 p-3 sm:mx-4 sm:p-4">
          <AcademicPlanFeatureGate featureName="Canopy" isRestricted={isPremiumDetailFeatureRestricted}>
            <CanopyTabContent row={bottomDetailRow} expanded />
          </AcademicPlanFeatureGate>
        </div>
      ) : showHistoricalScoreTab && activeBottomDetailTab === 'historical' ? (
        <div className="ml-2 mr-5 mb-4 mt-2 min-h-[200px] rounded-xl border border-slate-200 bg-white p-3 sm:mx-4 sm:p-4">
          <AcademicPlanFeatureGate
            featureName="Historical score"
            isRestricted={isPremiumDetailFeatureRestricted}
          >
            <HistoricalScoreTabContent
              row={bottomDetailRow}
              source={String(source)}
              tab={String(tab)}
              ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
              onRevealEvidenceTable={onRevealEvidenceTable}
            />
          </AcademicPlanFeatureGate>
        </div>
      ) : showEvidenceProfileTab && activeBottomDetailTab === 'evidence' ? (
        <div className="ml-2 mr-5 mb-4 mt-2 rounded-xl border border-slate-200 bg-white p-3 sm:mx-4 sm:p-4">
          <AcademicPlanFeatureGate
            featureName="Evidence profile"
            isRestricted={isPremiumDetailFeatureRestricted}
          >
            <EvidenceProfileTabContent
              row={bottomDetailRow}
              source={String(source ?? '')}
              tab={String(tab ?? '')}
              onClose={onClose}
              ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </AcademicPlanFeatureGate>
        </div>
      ) : (
        <div className="flex flex-col gap-3 overflow-x-auto p-3 sm:p-4 ml-2 mr-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-nowrap">
            {groupedBottomDetails.map(group => (
              <section
                key={`bottom-${group.title}`}
                className="flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 pt-0 pb-3 sm:min-w-[18rem] sm:shrink-0 sm:flex-1 sm:basis-0 lg:min-w-0"
              >
                <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 text-xs font-semibold uppercase tracking-widest text-slate-600 py-2 px-3 bg-gradient-to-r from-pink-50 to-white rounded-t-xl border-b border-slate-200">
                  <span className="min-w-0 truncate">• {group.title}</span>
                  <span
                    className="shrink-0 tabular-nums font-light text-gray-500"
                    aria-label={`${group.entries.length} fields in this group`}
                    style={{ fontSize: '0.7rem' }}
                  >
                    {group.entries.length}
                  </span>
                </h3>
                <div className="space-y-1.5 px-3 pt-2">
                  {group.entries.map(entry => {
                    return (
                      <div
                        key={`${group.title}-${entry.key}`}
                        className="mb-3 flex flex-col gap-1 text-start text-xs"
                      >
                        <span
                          className="overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                          style={{ fontSize: '0.65rem' }}
                          title={entry.displayLabel}
                        >
                          {entry.displayLabel}
                        </span>
                        {renderEntryValue(entry)}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
